/**
 * The provenance footer: whose hours the denominator is, what it does and does not deduct,
 * which resources the ratio actually covers, and whether the two halves of that ratio were
 * computed on the same model.
 *
 * The redesign has no block for these, and they cannot simply go: the denominator on this
 * surface once HALVED, and the first question in the room was "says who?" — a number that
 * cannot answer it is not a number anyone should plan against. So they sit at the foot of
 * the card as footnotes, quiet while everything is coherent, and escalate to a banner when
 * it is not (gap G25: load smeared at one basis and divided by another is an artefact of the
 * pairing, not a measurement).
 *
 * THE RULE THEY OBEY: a note must never take down the surface it annotates. `scheduling` is
 * required by the API contract, and the first version of its note read the field unguarded —
 * a backend serving the pre-G25 payload took the whole Capacity page down through the
 * ErrorBoundary. A missing footnote is a missing sentence; a crash is a broken product.
 */

import React from 'react'
import { AlertTriangle } from 'lucide-react'
import type { LoadSchedulingBasis, LoadVsCapacityView } from '../../types/loading'

const QUIET = 'font-sans text-[10.5px] leading-[1.5]'

/** The mart's entity key as the site is named on screen. At Group scope this component
 *  renders one block PER ENTITY, so a hardcoded site name here does not just read oddly on
 *  the Oracle page — it mislabels one of two blocks sitting directly above each other. */
const SITE_NAMES: Record<string, string> = { kirkstall: 'Kirkstall', oracle: 'Oracle' }
const siteOf = (entity: string): string => SITE_NAMES[entity.toLowerCase()] ?? entity

function Loud({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      className="flex items-start gap-2 rounded-[9px] border px-3 py-2 font-sans text-[11.5px] leading-[1.5]"
      style={{ borderColor: 'var(--lc-amber-line)', background: 'var(--lc-amber-bg)', color: 'var(--lc-ink3)' }}
    >
      <AlertTriangle size={14} className="mt-[2px] shrink-0" style={{ color: 'var(--lc-amber)' }} />
      <span>{children}</span>
    </div>
  )
}

/**
 * WHICH WORK the book counts — and it goes FIRST, above the denominator, because it defines
 * what every number on the surface is a measure of.
 *
 * It is deliberately NOT a `Loud` amber banner. Holding out child works orders at `WL` and
 * work on hold is a correct decision resting on Factory Master's own completion history, not
 * a defect to warn about. But it removes ~43% of the scheduled hours, so it cannot be a
 * bottom-of-the-page footnote either: a reader comparing this surface against a Factory
 * Master works-order list must be able to see, without asking, why the totals differ.
 */
function PopulationLine({ v }: { v: LoadVsCapacityView }): React.JSX.Element | null {
  const s = v.population_scope
  if (!s || !s.exclusions.length) return null
  return (
    <div className="flex flex-col gap-1 font-sans text-[11px] leading-[1.55]" style={{ color: 'var(--lc-ink3)' }}>
      <p>
        This is the work {siteOf(v.entity)} has to <b>deliver</b>:{' '}
        <b className="tabular-nums">{Math.round(s.served_hours).toLocaleString('en-GB')}h</b>. A further{' '}
        <b className="tabular-nums">{Math.round(s.withheld_hours).toLocaleString('en-GB')}h</b>
        {s.withheld_pct != null && <> ({s.withheld_pct}% of the scheduled book)</>} is excluded:
      </p>
      {/* A vertical list, not a run-on sentence. Each reason is two or three short sentences of
          its own, and threading them into one line produced a paragraph nobody would finish. */}
      <ul className="flex flex-col gap-0.5 pl-3.5">
        {s.exclusions.map((e) => (
          <li key={e.load_class} className="list-disc" style={{ color: 'var(--lc-mut)' }}>
            <b className="tabular-nums" style={{ color: 'var(--lc-ink3)' }}>
              {Math.round(e.load_hours).toLocaleString('en-GB')}h
            </b>{' '}
            {e.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** WHOSE hours, and what the week does not deduct. */
function CapacityLine({ v }: { v: LoadVsCapacityView }): React.JSX.Element {
  const src = v.capacity_source
  if (!src) {
    return (
      <p className={QUIET} style={{ color: 'var(--lc-mut)' }}>
        Capacity is <b>{v.basis_label}</b> — a 24-hour clock across weekdays, not hours anyone has told
        us the machines run.
      </p>
    )
  }
  return (
    <p className={QUIET} style={{ color: 'var(--lc-mut)' }}>
      Capacity is <b>{v.basis_label}</b> — {src.machines} machines totalling{' '}
      <b className="tabular-nums">{src.weekly_hours.toLocaleString('en-GB')}h</b> in a standard week
      {src.derate_pct !== null && <>, derated to {Math.round(src.derate_pct)}%</>}. Source: {src.attribution}.
      {src.exceptions_modelled
        ? ` Bank holidays, the shutdown and planned maintenance are deducted from the weeks they fall in${src.maintenance_uncovered.length ? ` — except for ${src.maintenance_uncovered.join(', ')}, which carry the site closures but no calibration downtime` : ''}.`
        : ' ⚠ Standard weeks only: no working calendar reached this view, so shutdowns, bank holidays and maintenance are not deducted from any week.'}
    </p>
  )
}

/**
 * The rate the schedule consumed work at.
 *
 * A RANGE, not a mean, whenever the smear is per machine (G10, 19 Aug 2026). The API sends
 * `weekly_hours` as the mean of the rows in view, and printing it would say "105h per
 * machine per week" of an estate whose machines run 44 to 92 — which is the exact
 * single-rate assumption the change removed, reinstated in the sentence describing its
 * removal. The fallback count goes with it: a reader has to be able to tell how much of the
 * book was priced at a real machine's speed and how much at the governed estate rate.
 *
 * ⚠ AND THE RANGE STOPS AT 92, NOT 120 (20 Aug 2026). The upper end used to be the FALLBACK
 * — the calendar_24h clock standing in for 781 rows on benches and inspection — so the
 * sentence read "44–120h per machine per week" and 120 looked like somebody's machine. It is
 * not a machine's week at all. The range is now real machines only, and the fallback is named
 * beside it as what it is.
 */
function Rate({ s }: { s: LoadSchedulingBasis }): React.JSX.Element {
  if (!s.is_per_machine || s.min_weekly_hours == null) {
    return (
      <>
        {s.weekly_hours.toLocaleString('en-GB')}h per machine per week (
        {s.hours_per_calendar_day}h per calendar day)
      </>
    )
  }
  return (
    <>
      each operation at its own machine&apos;s rate ({s.min_weekly_hours.toLocaleString('en-GB')}–
      {s.max_weekly_hours?.toLocaleString('en-GB')}h per machine per week)
      {s.fallback_rows > 0 && (
        <>
          . {s.fallback_rows.toLocaleString('en-GB')} row(s) sit on resources with no machine
          calendar and used the governed estate rate
          {s.fallback_weekly_hours != null && <> of {s.fallback_weekly_hours.toLocaleString('en-GB')}h per week</>}
        </>
      )}
    </>
  )
}

/** Both halves of the ratio, on the record (G25). */
function SchedulingLine({ v }: { v: LoadVsCapacityView }): React.JSX.Element | null {
  const s = v.scheduling
  if (!s) return null
  if (s.is_governed && s.matches_capacity_basis) {
    return (
      <p className={QUIET} style={{ color: 'var(--lc-mut)' }}>
        Load is scheduled on the same basis it is measured against — <b>{v.basis_label}</b>,{' '}
        <Rate s={s} />.
      </p>
    )
  }
  return (
    <Loud>
      {s.is_governed ? (
        <>
          Load was scheduled on the <b>{s.basis}</b> basis ({s.weekly_hours.toLocaleString('en-GB')}h per
          machine per week) but is measured against <b>{v.basis_label}</b>. These percentages compare two
          different models — read them as a denominator sensitivity, not as capacity.
        </>
      ) : (
        <>
          Load was scheduled on {s.weekly_hours.toLocaleString('en-GB')}h per machine per week, which is no
          longer the governed basis. The forward schedule predates the change and needs rebuilding before
          these percentages mean anything.
        </>
      )}
    </Loud>
  )
}

/**
 * Rows that COUNT hours they have no denominator for.
 *
 * The capacity denominator covers CNC machines only (spec §10e) while a cell sums every hour
 * in its resource group, so a group mixing a machine with a bench divides mixed load by
 * machine-only capacity and reads high. No Kirkstall group mixes. Two Oracle groups do —
 * `3 AXIS MILLING` reads 730% where the like-for-like figure is 410%, because Red Ant's own
 * `WKCentreGroups` puts `VF1 3AX` in a milling group and Red Ant also marks it `Active =
 * False` with no shift pattern at all.
 *
 * It is a `Loud` banner rather than a footnote: unlike the population exclusions, this is not
 * a correct decision that needs explaining — it is a percentage that is overstated by a known
 * amount, and a reader has no way to see it from the row.
 */
function MixedRows({ v }: { v: LoadVsCapacityView }): React.JSX.Element | null {
  const mixed = v.rows.filter((r) => (r.unmeasured_load_hours ?? 0) > 0)
  if (!mixed.length) return null
  return (
    <Loud>
      {mixed.length === 1 ? 'One group counts hours' : `${mixed.length} groups count hours`} it has
      no capacity for, so {mixed.length === 1 ? 'its' : 'their'} percentage reads high:{' '}
      {mixed.map((r, i) => (
        <React.Fragment key={r.resource_group}>
          {i > 0 && ', '}
          <b>{r.resource_group}</b>{' '}
          <span className="tabular-nums">
            {Math.round(r.unmeasured_load_hours ?? 0).toLocaleString('en-GB')}h
          </span>{' '}
          of {Math.round(r.total_load_hours).toLocaleString('en-GB')}h
        </React.Fragment>
      ))}
      . Those hours sit on resources with no machine calendar, so they are in the numerator and
      not the denominator.
    </Loud>
  )
}

export default function BasisNotes({ views }: { views: LoadVsCapacityView[] }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2 border-t px-[22px] py-3.5" style={{ borderColor: 'var(--lc-b07)' }}>
      {views.map((v) => (
        <div key={v.entity} className="flex flex-col gap-2">
          <PopulationLine v={v} />
          <CapacityLine v={v} />
          <SchedulingLine v={v} />
          <MixedRows v={v} />
          {v.population.process_resources > 0 && (
            <p className={QUIET} style={{ color: 'var(--lc-mut)' }}>{v.population.note}.</p>
          )}
        </div>
      ))}
      <p className={QUIET} style={{ color: 'var(--lc-faint)' }}>
        {/* This paragraph sits OUTSIDE the per-entity map, so it renders once for the whole
            surface. It used to end "read any single week's load as ±25%" — a band measured by
            fct_routing_confidence, which is Kirkstall only — so on Oracle it asserted a tolerance
            for a site with no grading at all. The band now lives in the confidence panel, beside
            the grading it comes from. What stays here is true of both sites. */}
        Levelling is a burn-down projection, <b>not a finite schedule</b>: it does not re-date the
        downstream operations of a works order whose upstream leg has slipped, and it does not read
        routing precedence. See load confidence, above, for how far the standards behind these
        hours can be trusted at this site.
      </p>
    </div>
  )
}
